<template>
  <div class="py-5 md:px-5">
    <PageHeader class="max-w-2xl" title="Edit Record" :back-step="true" />

    <div class="max-w-2xl mx-auto">
      <div class="card">
        <div class="p-5">
          <form @submit.prevent="saveRecord">
            <div class="-mx-2 lg:flex">
              <fieldset class="flex-1 px-2 form-group">
                <label for="date" class="form-label">Date</label>
                <DatePicker 
                  class="w-full"
                  color="indigo" 
                  mode="date" 
                  :model-config="{ timeAdjust: '12:00:00' }"
                  v-model="form.date">
                  <template v-slot="{ inputValue, inputEvents }">
                    <input type="text" class="form-input" :value="inputValue" v-on="inputEvents">
                  </template>
                </DatePicker>
              </fieldset>
              <fieldset class="flex-1 px-2 form-group">
                <label for="odometer" class="form-label">Odometer</label>
                <div class="relative rounded-md">
                  <input id="odometer" class="w-full pr-8 form-input" placeholder="0" v-model="form.odometer" />
                  <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <span class="text-gray-400">
                      km
                    </span>
                  </div>
                </div>
              </fieldset>
            </div>
            <div class="-mx-2 lg:flex">
              <fieldset class="flex-1 px-2 form-group">
                <label for="vehicle" class="form-label">Vehicle</label>
                <vehicle-select v-model="form.vehicle_id" />
              </fieldset>
              <fieldset class="flex-1 px-2 form-group">
                <label for="vehicle" class="form-label">Vehicle Group</label>
                <group-select v-model="form.vehicle_group_id" type="vehicle" />
              </fieldset>
            </div>
            <div class="-mx-2 lg:flex">
              <fieldset class="flex-1 px-2 form-group">
                <label for="vendor" class="form-label">Vendor</label>
                <vendor-select v-model="form.vendor_id" />
              </fieldset>
              <fieldset class="flex-1 px-2 form-group">
                <label for="reference" class="form-label">Reference</label>
                <input type="text" id="reference" class="form-input" v-model="form.reference">
              </fieldset>
            </div>
            <div class="-mx-2 lg:flex">
              <fieldset class="flex-1 px-2 form-group">
                <label for="category" class="form-label">Category</label>
                <category-select id="category" v-model="form.category_id" />
              </fieldset>
              <fieldset class="flex-1 px-2 form-group">
                <label for="amount" class="form-label">Amount</label>
                <div class="relative rounded-md">
                  <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <span class="text-gray-400">
                      {{ account.currency }}
                    </span>
                  </div>
                  <currency-input id="amount" class="w-full pl-8 text-right form-input" placeholder="0.00" :currency="null" auto-decimal-mode value-as-integer v-model="form.amount" />
                </div>
              </fieldset>
            </div>
            <div>
              <fieldset class="form-group">
                <label for="notes" class="form-label">Notes</label>
                <textarea id="notes" class="w-full form-textarea" v-model="form.notes"></textarea>
              </fieldset>
            </div>
            <div>
              <fieldset class="form-group">
                <label for="attachments" class="form-label">Attachments</label>
                <div class="mb-2 divide-y">
                  <div class="flex items-center" v-for="file in form.files" :key="file.id">
                    <button class="py-2 text-left truncate hover:underline" @click="showFile(file.id)">
                      <fa :icon="['far', 'file-alt']" class="mr-3 text-gray-500" />{{ file.name }}
                    </button>
                    <button type="button" class="px-3 py-2 ml-auto text-sm text-red-500 hover:text-red-400" @click="removeFile(file.id)">
                      Remove
                    </button>
                  </div>
                </div>
                <file-uploader 
                  id="attachments" 
                  bucket="fleetbeacon-records" 
                  @file-added="uploading = true"
                  @file-removed="detachFile"
                  @processing="uploading = true"
                  @complete="uploading = false"
                  @error="uploading = false"
                  @success="attachFile" />
              </fieldset>
            </div>
            <div class="flex justify-end">
              <button class="btn btn-primary" v-submitting="submitting || uploading">Save Changes</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import DatePicker from 'v-calendar/lib/components/date-picker.umd'
import FileUploader from '@/components/FileUploader'
import CategorySelect from '@/components/CategorySelect'
import VendorSelect from '@/components/VendorSelect'
import GroupSelect from '@/components/GroupSelect'
import VehicleSelect from '@/components/VehicleSelect'

export default {
  name: 'RecordEdit',

  components: {
    DatePicker,
    FileUploader,
    CategorySelect,
    VendorSelect,
    GroupSelect,
    VehicleSelect
  },

  async created () {
    this.getRecord()
  },

  computed: {
    ...mapGetters(['account'])
  },

  data () {
    return {
      loading: true,
      uploading: false,
      submitting: false,
      form: {
        date: new Date(),
        amount: null,
        vendor_id: '',
        vehicle_id: '',
        tag_id: '',
        reference: '',
        odometer: null,
        category: '',
        note: '',
        attachments: []
      }
    }
  },

  methods: {
    /**
     * When we get the record from the database, it already has "form.files"
     * When we post new files on a record we call those "form.attachments"
     * This means we are free to delete attachments individually 
     * without worrying about saving the entire form
     */
    attachFile (data) {
      this.form.attachments.push(data.file.id)
    },

    detachFile (file) {
      // this.$delete(this.form.attachments, 2)
    },
    
    async getRecord () {
      try {
        const { data } = await this.$api.get(`account/${this.$route.params.account}/records/${this.$route.params.id}`)
        this.form = data.record
        this.form.attachments = []
        this.form.date = new Date(data.record.date)
        this.loading = false
      } catch (error) {
        console.error(error)
      }
    },

    async saveRecord () {
      try {
        this.submitting = true
        await this.$api.patch(`account/${this.$route.params.account}/records/${this.$route.params.id}`, this.form)
        this.$store.dispatch('syncUser', this)
        this.$router.push({ name: 'RecordList' })
      } catch (error) {
        console.error(error)
      } finally {
        this.submitting = false
      }
    },

    async deleteRecord () {
      if (window.confirm('Are you sure you want to delete this record?')) {
        try {
          this.submitting = true
          await this.$api.delete(`account/${this.$route.params.account}/records/${this.$route.params.id}`)
          this.$store.dispatch('syncUser', this)
          this.$router.push({ name: 'RecordList' })
        } catch (error) {
          console.error(error)
        } finally {
          this.submitting = false
        }
      }
    },

    async removeFile (id) {
      if (window.confirm('Are you sure you want to remove this file from this record? The file will still be available from the Files section.')) {
        try {
          this.submitting = true
          await this.$api.delete(`account/${this.$route.params.account}/record-files/${id}`)
          const index = this.form.files.findIndex(file => file.id === id)
          this.$delete(this.form.files, index)
          this.$toasted.show('File removed from record.')
        } catch (error) {
          console.error(error)
        } finally {
          this.submitting = false
        }
      }
    },

    async showFile (id) {
      try {
        const { data } = await this.$api.get(`account/${this.$route.params.account}/files/${id}`)
        window.open(data.url, '_blank')
      } catch (error) {
        console.error(error)
      }
    }
  }
}
</script>